"use client";

import Image from "next/image";
import Link from "next/link";
import { Suspense } from "react";
import { ArrowRight, Smartphone, CreditCard, RotateCcw, CheckCircle2, Sparkles, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";

import { InteractiveShowcase } from "@/components/landing/interactive-showcase";
import { FAQSection } from "@/components/landing/faq-section";
import { LandingPricingSection } from "@/components/landing/landing-pricing-section";
import { useLandingCta } from "@/hooks/use-landing-cta";

const VIRTUAL_SHOWCASE = [
  {
    name: "Sports Car",
    before: "/showcase/images/before5.webp",
    imageClassName: "object-[center_bottom]",
    wheels: [
      {
        id: "virt-1",
        name: "Try Style A",
        image: "/showcase/images/after51.webp",
        thumbnail: "/showcase/images/after51.webp",
      },
      {
        id: "virt-2",
        name: "Try Style B",
        image: "/showcase/images/after52.webp",
        thumbnail: "/showcase/images/after52.webp",
      },
      {
        id: "virt-3",
        name: "Try Style C",
        image: "/showcase/images/after53.webp",
        thumbnail: "/showcase/images/after53.webp",
      },
    ],
  },
  {
    name: "Sedan",
    before: "/showcase/images/before1.jpg",
    imageClassName: "object-[center_70%]",
    wheels: [
      {
        id: "virt-4",
        name: "Try Style A",
        image: "/showcase/images/after11.webp",
        thumbnail: "/showcase/images/after11.webp",
      },
      {
        id: "virt-5",
        name: "Try Style B",
        image: "/showcase/images/after12.webp",
        thumbnail: "/showcase/images/after12.webp",
      },
      {
        id: "virt-6",
        name: "Try Style C",
        image: "/showcase/images/after13.webp",
        thumbnail: "/showcase/images/after13.webp",
      },
    ],
  },
];

const TRY_ON_BENEFITS = [
  {
    icon: <CreditCard className="h-5 w-5" />,
    title: "Save money",
    description: "Wheels cost $1,000–$5,000+. See exactly what you're getting before spending. No expensive mistakes.",
  },
  {
    icon: <RotateCcw className="h-5 w-5" />,
    title: "Skip the returns",
    description: "Returning wheels is a hassle-shipping, restocking fees, wait times. Get it right the first time.",
  },
  {
    icon: <Clock className="h-5 w-5" />,
    title: "Instant results",
    description: "No waiting for test-fits at a shop. Try wheels virtually in under a minute, from anywhere.",
  },
];

const COMPARISON_POINTS = [
  {
    traditional: "Drive to shop, wait for mounting",
    virtual: "Upload photo from your couch",
  },
  {
    traditional: "Try 2-3 options at most",
    virtual: "Explore more options",
  },
  {
    traditional: "Pay for test-fits or rely on salesperson",
    virtual: "See realistic previews instantly",
  },
  {
    traditional: "Can't compare side-by-side easily",
    virtual: "Download and compare all options",
  },
];

const VIRTUAL_FAQS = [
  {
    question: "How is this different from a wheel configurator?",
    answer:
      "Most wheel configurators show wheels on generic car images from their library. Virtual fitting uses YOUR actual car photo-your color, your trim, your environment. The result is personalized and realistic.",
  },
  {
    question: "Is this like virtual try-on for glasses?",
    answer:
      "Same concept, different product. Just like you'd virtually try on glasses before buying, you can virtually try on wheels. Upload your car, select wheels, see how they look-then decide.",
  },
  {
    question: "Can I try wheels I found online?",
    answer:
      "Yes. Screenshot or save any wheel image you find-eBay, Instagram, manufacturer sites-and upload it. The AI fits it to your car automatically.",
  },
  {
    question: "How realistic are the virtual fittings?",
    answer:
      "Very. The AI matches perspective, lighting, and shadows to make the wheel look like it's actually on your car. Most users can't tell it's a preview, not a real photo.",
  },
  {
    question: "Can I share my virtual fittings?",
    answer:
      "Yes. Download HD images of any fitting-no watermarks. Share with friends, post on forums, or show your wheel shop what you want.",
  },
  {
    question: "Is there a limit to how many wheels I can try?",
    answer:
      "Each fitting uses one credit. Buy as many credits as you need. Most users try 5-10 options before deciding.",
  },
];

export function VirtualWheelFittingContent() {
  const { heroCtaRef, showStickyCta, ctaText, handleTryItNow } = useLandingCta();

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Virtual Wheel Fitting",
    url: "https://thewheelvisualizer.com/virtual-wheel-fitting",
    description:
      "Try wheels on your car virtually before buying. Like a fitting room for wheels.",
    mainEntity: {
      "@type": "SoftwareApplication",
      name: "The Wheel Visualizer",
      operatingSystem: "Web",
      applicationCategory: "GraphicsApplication",
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to virtually try wheels on your car",
    step: [
      {
        "@type": "HowToStep",
        name: "Upload your car photo",
        text: "Take a photo of your car and upload it to the virtual fitting tool.",
      },
      {
        "@type": "HowToStep",
        name: "Select wheels to try",
        text: "Choose from our library or upload your own wheel images.",
      },
      {
        "@type": "HowToStep",
        name: "See the virtual fitting",
        text: "AI generates a realistic preview showing how the wheels look on your car.",
      },
    ],
  };

  return (
    <div className="bg-[#04060A] text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webPageSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] to-transparent" />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
                <Smartphone className="h-4 w-4 text-white/60" />
                <span className="text-sm text-white/70">Virtual try-on</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
                Virtual Wheel Fitting
                <br />
                <span className="text-white/70">Try Before You Buy</span>
              </h1>
              
              <p className="text-lg text-white/60 mb-4 leading-relaxed">
                Like a fitting room for wheels. Upload your car photo, try different wheels virtually, and see exactly what you&apos;re getting before spending thousands.
              </p>
              <p className="text-white/80 font-medium mb-8">
                No mounting, no hassle, no returns.
              </p>

              <div className="flex flex-col sm:flex-row gap-4" ref={heroCtaRef}>
                <Button
                  size="lg"
                  onClick={handleTryItNow}
                  className="bg-white text-black hover:bg-white/90 font-semibold h-12 px-8"
                >
                  {ctaText}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="border-white/20 text-white hover:bg-white/5 h-12 px-8"
                >
                  <Link href="/credits">View Pricing</Link>
                </Button>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-8">
                <div className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">From Anywhere</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">&lt;60s Per Fitting</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/40" />
                  <span className="text-white/70">HD Download</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
                <Image
                  src="/showcase/after4.webp"
                  alt="Virtual wheel fitting result"
                  fill
                  className="object-cover"
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  priority
                />
              </div>
              <div className="absolute -bottom-4 -left-4 px-4 py-3 rounded-xl bg-[#0A0C0F] border border-white/10">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-white/60" />
                  <span className="text-sm text-white/80">Try more wheel styles</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why virtual fitting */}
      <section className="py-24 px-6 bg-[#080A0D]">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Why try virtually</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              The smart way to shop for wheels
            </h2>
            <p className="text-white/60 mt-4 max-w-2xl">
              Wheels are a big purchase. Virtual fitting lets you see exactly what you&apos;re getting-no guesswork, no expensive mistakes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {TRY_ON_BENEFITS.map((benefit) => (
              <div key={benefit.title} className="group">
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 text-white/70 group-hover:text-white group-hover:border-white/20 transition-colors">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                <p className="text-white/60 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Virtual vs Traditional comparison */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Comparison</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Traditional vs Virtual fitting
            </h2>
          </div>

          <div className="space-y-4">
            {COMPARISON_POINTS.map((point, idx) => (
              <div key={idx} className="grid grid-cols-2 gap-4">
                <div className="p-5 rounded-xl bg-white/[0.02] border border-white/5">
                  <p className="text-white/50 text-sm">{point.traditional}</p>
                </div>
                <div className="p-5 rounded-xl bg-white/[0.04] border border-white/10">
                  <p className="text-white/80 text-sm">{point.virtual}</p>
                </div>
              </div>
            ))}
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="text-center text-xs text-white/30 uppercase tracking-wide">Traditional</div>
              <div className="text-center text-xs text-white/50 uppercase tracking-wide">Virtual Fitting</div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive showcase */}
      <section className="py-24 px-6 bg-[#080A0D] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Try it yourself</p>
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              See how virtual fitting works
            </h2>
            <p className="text-white/60 mt-4">
              Drag the slider to compare. Tap thumbnails to virtually try different wheels.
            </p>
          </div>

          <div className="space-y-16">
            {VIRTUAL_SHOWCASE.map((scenario, idx) => (
              <div key={scenario.name}>
                <InteractiveShowcase
                  beforeImage={scenario.before}
                  wheels={scenario.wheels}
                  imageClassName={scenario.imageClassName}
                  priority={idx === 0}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24 px-6 bg-[#05070C] border-t border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-white/10">
              <Image
                src="/showcase/after2.webp"
                alt="Virtual fitting example"
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
              <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-white/90 text-black text-sm font-medium">
                Virtual fit result
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">Simple process</p>
              <h2 className="text-3xl font-semibold text-white mb-6">
                Three steps to try any wheel
              </h2>
              <ol className="space-y-6">
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-sm font-medium text-white/70">
                    1
                  </div>
                  <div>
                    <div className="text-white font-medium mb-1">Upload your car</div>
                    <p className="text-white/60 text-sm">Phone photo works fine. Side profile or 3/4 angle is best.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-sm font-medium text-white/70">
                    2
                  </div>
                  <div>
                    <div className="text-white font-medium mb-1">Select wheels to try</div>
                    <p className="text-white/60 text-sm">Browse our library or upload any wheel image you&apos;ve found online.</p>
                  </div>
                </li>
                <li className="flex gap-4">
                  <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center shrink-0 text-sm font-medium text-white/70">
                    3
                  </div>
                  <div>
                    <div className="text-white font-medium mb-1">See the result</div>
                    <p className="text-white/60 text-sm">AI generates a realistic preview in under 60 seconds. Download and compare.</p>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 px-6 bg-[#04060A]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-white">
              Virtual fitting questions
            </h2>
            <p className="text-white/60 mt-4">
              What users ask about trying wheels virtually.
            </p>
          </div>
          <FAQSection faqs={VIRTUAL_FAQS} />
        </div>
      </section>

      <Suspense fallback={<div className="py-24 px-6 bg-[#0A0C0F]" />}>
        <LandingPricingSection />
      </Suspense>

      {/* Sticky CTA */}
      {showStickyCta && (
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-[#05070C]/95 backdrop-blur border-t border-white/10 pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-3">
            <button
              onClick={handleTryItNow}
              className="w-full flex items-center justify-center gap-2 h-12 rounded-xl bg-white text-black font-semibold"
            >
              {ctaText}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
